/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useContext } from 'react';
import { Box, Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createTheme,ThemeProvider } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { useQuery, useLazyQuery } from '@apollo/client';
import * as Q from '../../../../../constants/graphqlQueries';
import CustomDropdown from '../../../../components/Dropdown/CustomDropdown';
import CustomButtons from '../../../../components/Button/CustomButtons';
import colors from '../../../../../constants/colors';
import CustomTextField from '../../../../components/Textfield/CustomTextField';
import { TeamAlertsContext } from '../../../../../context/team-alerts-context';
import { makeStyles } from '@mui/styles';

const theme = createTheme({
  overrides: {
    MuiOutlinedInput: {
      root: {
        '& $notchedOutline': {
          borderColor: '#D5D5D5',
        },
        '&:hover $notchedOutline': {
          borderColor: '#D5D5D5',
        },
        '&$focused $notchedOutline': {
          borderColor: '#D5D5D5',
        },
      },
    },
  },
});
const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column'
  },
  mainBox: {
    margin: '0 14px',
    paddingBottom: '4px',
  },
  title: {
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    color: colors.blue,
    marginTop: '8px',
    marginBottom: '4px',
    fontSize: '1.6rem',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    paddingBottom: '6px',
  },
  rowFooter: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '4px',
    marginTop: '8px',
  }
}));

const FieldIndex = {
    client: 1,
    program: 2,
    project: 3,
    site: 4,
    agentName: 10,
    agentOracleId: 11
}

const accordionOptions = {
  CPPS: 'CPPS',
  Agent: 'Agent',
  TL: 'TL'
}

const AccordionSummaryCustom = withStyles({
  root: {
    minHeight: '5vh',
    '&$expanded': {
      minHeight: '0px',
      paddingTop: '2vh'
    }
  },
  content: {
    margin: '4px 0',
    '&$expanded': {
      margin: '4px 0'
    }
  },
  expandIcon: {
    padding: 3
  },
  expanded: {}
})(AccordionSummary);

// Defensive trim for dropdown labels: source data may contain stray whitespace
// in codes/descriptions (e.g. ClientId "LPF7 " -> "(LPF7 )", BUG-WFA2-7144-003).
// Display-only: the underlying option values are intentionally left untouched so
// queries keyed on them still match the (currently un-trimmed) DB values.
const formatOptionLabel = (description, id) => `${String(description ?? '').trim()} (${String(id ?? '').trim()})`;

const Search = ({ onSearchClick, onClear, showAgentAccordion, buttonName, tooltipSearchMessage }) => {
  const classes = useStyles();
  const { setSearchOptions } = useContext(TeamAlertsContext);
  const [expandedAccordion, setExpandedAccordion] = useState(accordionOptions.CPPS);
  const [disabledFieldIndex, setDisabledFieldIndex] = useState(FieldIndex.program);
  const [selectedClient, setSelectedClient] = useState();
  const [selectedProgram, setSelectedProgram] = useState();
  const [selectedProject, setSelectedProject] = useState();
  const [selectedSite, setSelectedSite] = useState();

  const [selectedAgentOracleId, setSelectedAgentOracleId] = useState('');
  const [selectedAgentName, setSelectedAgentName] = useState('');
  const [selectedSupervisors, setSelectedSupervisors] = useState();

  const [clients, setClients] = useState({});
  const [programs, setPrograms] = useState({});
  const [projects, setProjects] = useState({});
  const [sites, setSites] = useState({});
  const [supervisors, setSupervisors] = useState({});
  const [disabledButton, setDisabledButton] = useState(true);

  // lazy queries
  const [getPrograms, { data: dataPrograms }] = useLazyQuery(Q.APP_ALERT_PROGRAMS_GL_QUERY);
  const [getProjects, { data: dataProjects }] = useLazyQuery(Q.APP_ALERT_PROJECTS_GL_QUERY);

  const { data: dataSupervisors } = useQuery(Q.SUPERVISORS_GA_QUERY);
  const { data: dataClients } = useQuery(Q.APP_ALERT_CLIENTS_GL_QUERY);
  const { data: dataSites } = useQuery(Q.APP_ALERT_SITES_GL_QUERY);

  useEffect(() => {
    if (dataSupervisors) {
      if (dataSupervisors.supervisors) {
        const supervisorProps = {
          options: dataSupervisors.supervisors,
          getOptionLabel: (option) => option.SupervisorEmployeeData
        }
        setSupervisors(supervisorProps);
      }
    }
  }, [dataSupervisors]);

  useEffect(() => {
    if (dataProjects?.getProjects) {
      const projectProps = {
        options: dataProjects.getProjects,
        getOptionLabel: (option) => option.ProjectId === 'All' ? option.DescriptionShort : formatOptionLabel(option.DescriptionShort, option.ProjectId)
      }
      projectProps.options.unshift({
        ClientId: 'All',
        DescriptionShort: 'All',
        ProgramId: 'All',
        ProjectId: 'All'
      });
      setProjects(projectProps);
    }
  }, [dataProjects]);

  useEffect(() => {
    if (dataPrograms?.getPrograms) {
      const programProps = {
        options: dataPrograms.getPrograms,
        getOptionLabel: (option) => option.ProgramId === 'All' ? option.DescriptionShort : formatOptionLabel(option.DescriptionShort, option.ProgramId)
      }
      programProps.options.unshift({
          ClientId: 'All',
          DescriptionShort: 'All',
          ProgramId: 'All' 
        });
      setPrograms(programProps);
    }
  }, [dataPrograms]);

  useEffect(() => {
    if (dataClients?.getClients) {
      // Deduplicate clients by DescriptionShort
      const seen = new Set();
      const uniqueClients = dataClients.getClients.filter((c) => {
        if (seen.has(c.DescriptionShort)) return false;
        seen.add(c.DescriptionShort);
        return true;
      });
      const clientProps = {
        options: [{ ClientId: 'All', DescriptionShort: 'All' }, ...uniqueClients],
        getOptionLabel: (option) => option.ClientId === 'All' ? option.DescriptionShort : formatOptionLabel(option.DescriptionShort, option.ClientId)
      }
      setClients(clientProps);
    }
  }, [dataClients]);

  useEffect(() => {
    if (dataSites?.getSites) {
      const siteProps = {
        options: dataSites.getSites,
        getOptionLabel: (option) => option.SiteId === 'All' ? option.DescriptionShort : formatOptionLabel(option.DescriptionShort, option.SiteId)
      }
      siteProps.options.unshift({
        SiteId: 'All',
        DescriptionShort: 'All'
      });
      setSites(siteProps);
    }
  }, [dataSites]);

  useEffect(() => {
    if (selectedAgentName) {
      setDisabledFieldIndex(FieldIndex.client);
    }
    else if (selectedAgentOracleId) {
      setDisabledFieldIndex(FieldIndex.client);
    }
    else {
      setDisabledFieldIndex(FieldIndex.program);
    }
  }, [selectedAgentName, selectedAgentOracleId]);

  useEffect(() => {
    let fieldIndex = disabledFieldIndex;
    if (selectedClient) {
      fieldIndex = FieldIndex.project;
    }
    else {
      fieldIndex = FieldIndex.program;
    }

    if (selectedProgram) {
      fieldIndex = null;
    }

    setDisabledFieldIndex(fieldIndex);
  }, [selectedClient, selectedProgram, selectedProject, selectedSite]);

  const isFieldDisabled = (fieldPriorityNumber) => {
    let result = false;

    if (disabledFieldIndex <= fieldPriorityNumber && disabledFieldIndex) {
      result = true;
    }

    return result;
  }

  useEffect(() => {
    if (!disabledFieldIndex && selectedProgram) {
      setDisabledButton(false);
    }
  }, 
  [disabledFieldIndex, selectedClient, selectedProgram, selectedProject, selectedSite])

  const onClickSearch = () => {
    let selectedSupervisorEmployeeId = '';
    
    if (selectedSupervisors) {
      if (selectedSupervisors.SupervisorEmployeeId) {
        selectedSupervisorEmployeeId = selectedSupervisors.SupervisorEmployeeId;
      }
    }

    setSearchOptions({
      clientId: selectedClient ? selectedClient.ClientId : null,
      programId: selectedProgram ? selectedProgram.ProgramId : null,
      projectId: selectedProject ? selectedProject.ProjectId : null,
      siteId: selectedSite ? selectedSite.SiteId : null,
      agentOracleId: selectedAgentOracleId,
      agentName: selectedAgentName,
      supervisorOracleId: selectedSupervisorEmployeeId
    });

    onSearchClick(selectedClient, selectedProgram, selectedProject, selectedSite, selectedAgentOracleId, selectedAgentName, selectedSupervisorEmployeeId);
  }
  const onClearCPPS = (element) => {
    if (element === 4) {
      setSelectedClient(null);
      setSelectedProgram(null);
      setSelectedProject(null);
      setSelectedSite(null);
    }
    else if (element === 3) {
      setSelectedProgram(null);
      setSelectedProject(null);
      setSelectedSite(null);
    } 
    else if ( element === 2) {
      setSelectedProject(null);
      setSelectedSite(null);
    } 
    else {
      setSelectedSite(null);
    }
    setDisabledButton(true);
  }

  const onClearClick = () => {
    setSelectedAgentName('');
    setSelectedAgentOracleId('');
    setSelectedSupervisors(null);
    onClearCPPS(4);
    
    onClear();
  }

  const onChangeClient = (client) => {
    if (client) {
      setSelectedClient(client);
      onClearCPPS(3);

      getPrograms({ variables: { ClientId: client.ClientId } });
    }
    else {
      onClearCPPS(4);
    }
  };
  const onChangeProgram = (program) => {
    if (program) {
      setSelectedProgram(program);
      onClearCPPS(2);

      getProjects({ variables: { ProgramId: program.ProgramId } });
    }
    else {
      onClearCPPS(3);
    }
  };
  const onChangeProject = (project) => {
    if (project) {
      setSelectedProject(project);
      onClearCPPS(1);
    }
    else {
      onClearCPPS(2);
    }
  };
  const onChangeSite = (site) => {
    if (site) {
      setSelectedSite(site);
    }
    else {
      onClearCPPS(1);
    }
  };

  const onChangeAgentOracleId = (agentOracleId) => {
    setSelectedAgentOracleId(agentOracleId);
  }

  const onChangeAgentName = (agent) => {
    setSelectedAgentName(agent);
  }
  
  const onChangeSupervisor = (supervisor) => {
    setSelectedSupervisors(supervisor);
  }

  const onChangeAccordion = (option) => {
    if (option === accordionOptions.CPPS) {
      setSelectedAgentName('');
      setSelectedAgentOracleId('');
      setSelectedSupervisors(null);
    }
    else if (option === accordionOptions.Agent) {
      setSelectedClient(null);
      setSelectedProgram(null);
      setSelectedProject(null);
      setSelectedSite(null);
      setSelectedSupervisors(null);
    }
    else if (option === accordionOptions.TL) {
      setSelectedClient(null);
      setSelectedProgram(null);
      setSelectedProject(null);
      setSelectedSite(null);
      setSelectedAgentName('');
      setSelectedAgentOracleId('');
    }

    setDisabledButton(true);
    setExpandedAccordion(option);
  }

  return (
    <ThemeProvider theme={theme}>
      <Box className={classes.mainBox}>
        <Box className={classes.title}>
          Search
        </Box>
        <Accordion expanded={expandedAccordion === accordionOptions.CPPS} onChange={() => onChangeAccordion(accordionOptions.CPPS)}>
          <AccordionSummaryCustom expandIcon={<ExpandMoreIcon />}>CPPS</AccordionSummaryCustom>
          <AccordionDetails className={classes.container}>
            <CustomDropdown
              title='Client'
              value={selectedClient}
              handleChange={onChangeClient}
              defaultProps={clients}
              disabled={isFieldDisabled(FieldIndex.client)}
              mandatory
            />
            <CustomDropdown
              title='Program'
              value={selectedProgram}
              handleChange={onChangeProgram}
              defaultProps={programs}
              disabled={isFieldDisabled(FieldIndex.program)}
              mandatory
            />
            <CustomDropdown
              title='Project'
              value={selectedProject}
              handleChange={onChangeProject}
              defaultProps={projects}
              disabled={isFieldDisabled(FieldIndex.project)}
            />
            <CustomDropdown
              title='Site'
              value={selectedSite}
              handleChange={onChangeSite}
              defaultProps={sites}
              disabled={isFieldDisabled(FieldIndex.site)}
            />
          </AccordionDetails>
        </Accordion>
        {showAgentAccordion && <Accordion expanded={expandedAccordion === accordionOptions.Agent} onChange={() => onChangeAccordion(accordionOptions.Agent)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Agent</AccordionSummary>
          <AccordionDetails className={classes.container}>
            <CustomTextField
              title='Agent Name'
              value={selectedAgentName}
              handleChange={onChangeAgentName}
              placeholder='Input text'
              disabled={!!selectedClient || !!selectedAgentOracleId}
              mandatory={!selectedAgentOracleId}
            />
            <CustomTextField
              title='Agent Oracle Id'
              value={selectedAgentOracleId}
              handleChange={onChangeAgentOracleId}
              placeholder='Input text'
              disabled={!!selectedClient || !!selectedAgentName}
              mandatory={!selectedAgentName}
            />
          </AccordionDetails>
        </Accordion>}
        <Accordion expanded={expandedAccordion === accordionOptions.TL} onChange={() => onChangeAccordion(accordionOptions.TL)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Supervisors</AccordionSummary>
          <AccordionDetails className={classes.container}>
            <CustomDropdown
              title='Team Leads'
              value={selectedSupervisors}
              handleChange={onChangeSupervisor}
              defaultProps={supervisors}
            />
          </AccordionDetails>
        </Accordion>
        <Box className={classes.rowFooter}>
          <CustomButtons
            handleClick={() => onClearClick()}
            name='Clear'
          />
          <CustomButtons
            handleClick={() => onClickSearch()}
            name={buttonName}
            disabled={disabledButton && !selectedAgentName && !selectedAgentOracleId && !selectedSupervisors}
            tooltipFree
            tooltipMessage={tooltipSearchMessage}
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
}

Search.propTypes = {
  onSearchClick: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  showAgentAccordion: PropTypes.bool,
  buttonName: PropTypes.string,
  tooltipSearchMessage: PropTypes.string
}
Search.defaultProps = {
  showAgentAccordion: false,
  buttonName: 'Search',
  tooltipSearchMessage: null
}

export default Search;
